import { ArrowDown, MapPin, TrendingUp, ShieldCheck, KeyRound } from "lucide-react";
import { HERO_IMAGE } from "@/data/apartments";
import { useT } from "@/i18n/LanguageProvider";

export const InvestorHero = () => {
    const t = useT();
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section
            className="relative min-h-screen w-full overflow-hidden"
            data-testid="investor-hero-section"
        >
            <div className="absolute inset-0">
                <img
                    src={HERO_IMAGE}
                    alt="G21 Wiesbaden-Biebrich – Anlageobjekt"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/55 to-stone-900/40" />
                <div className="absolute inset-0 grain opacity-10 pointer-events-none" />
            </div>

            <div className="relative z-10 container-page flex flex-col justify-end min-h-screen pt-32 pb-20">
                <div className="max-w-3xl reveal-up">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-block w-12 h-px bg-clay-300" />
                        <span className="overline text-clay-200" data-testid="investor-hero-eyebrow">
                            {t({ de: "Kapitalanlage · Wiesbaden-Biebrich", en: "Investment · Wiesbaden-Biebrich" })}
                        </span>
                    </div>

                    <h1 className="h-display text-white text-balance">
                        {t({
                            de: <>Neubau als <span className="text-terracotta-300">Kapitalanlage.</span></>,
                            en: <>New build as a <span className="text-terracotta-300">capital investment.</span></>,
                        })}
                        <span className="block mt-3 text-2xl sm:text-3xl text-clay-200 font-body font-normal">
                            {t({ de: "5 % AfA · KfW-förderfähig · Vollservice", en: "5 % AfA · KfW-eligible · full service" })}
                        </span>
                    </h1>

                    <p className="body-lg text-white/85 mt-8 max-w-xl">
                        {t({
                            de: "Vier hochwertige Eigentumswohnungen direkt vom Bauträger – als Kapitalanlage besonders attraktiv: degressive AfA für Neubau, KfW-Förderung möglich, eigene Wärmepumpe je Wohnung. Auf Wunsch übernehmen wir Vermietung und laufende Verwaltung.",
                            en: "Four high-quality condominiums direct from the developer – particularly attractive as an investment: declining-balance AfA for new builds, KfW funding available, own heat pump per apartment. On request we also handle leasing and ongoing management.",
                        })}
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            onClick={() => scrollTo("afa-rechner")}
                            className="btn-primary"
                            data-testid="investor-cta-afa"
                        >
                            {t({ de: "AfA-Rechner öffnen", en: "Open AfA calculator" })}
                            <ArrowDown size={16} />
                        </button>
                        <button
                            onClick={() => scrollTo("vermietung")}
                            className="px-7 py-3.5 rounded-full border border-white/40 text-white font-body font-medium tracking-wide transition-all hover:bg-white hover:text-sage-700"
                            data-testid="investor-cta-rental"
                        >
                            {t({ de: "Vermietungs-Service", en: "Rental service" })}
                        </button>
                    </div>

                    <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/80 font-body text-sm">
                        <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-clay-300" />
                            <span>Gaugasse 21, 65203 Wiesbaden</span>
                        </div>
                        <span className="hidden sm:inline-block w-px h-4 bg-white/30" />
                        <span className="inline-flex items-center gap-2">
                            <TrendingUp size={14} className="text-clay-300" />
                            {t({ de: "5 % degressive AfA", en: "5 % declining-balance AfA" })}
                        </span>
                        <span className="hidden sm:inline-block w-px h-4 bg-white/30" />
                        <span className="inline-flex items-center gap-2">
                            <ShieldCheck size={14} className="text-clay-300" />
                            {t({ de: "KfW-förderfähig", en: "KfW-eligible" })}
                        </span>
                        <span className="hidden sm:inline-block w-px h-4 bg-white/30" />
                        <span className="inline-flex items-center gap-2">
                            <KeyRound size={14} className="text-clay-300" />
                            {t({ de: "Vermietungs-Service", en: "Rental service" })}
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};
